import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Search, Package, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const InventoryAdjustments = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewAdjustmentDialog, setShowNewAdjustmentDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  
  // Datos del ajuste
  const [adjustmentData, setAdjustmentData] = useState({
    tipo: 'ENTRADA',
    fecha: new Date().toISOString().split('T')[0],
    observaciones: ''
  });

  useEffect(() => {
    fetchProducts();
    fetchAdjustments();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchAdjustments = async () => {
    try {
      const response = await axios.get(`${API}/inventory-adjustments`);
      setAdjustments(response.data);
    } catch (error) {
      toast.error('Error al cargar ajustes');
    }
  };

  const filteredProducts = products.filter(p =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.codigo_interno.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.codigo_barras && (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.toLowerCase().includes(searchTerm.toLowerCase())) : p.codigo_barras.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.producto_id === product.id);
    
    if (existingItem) {
      toast.error('Este producto ya está en la lista');
      return;
    }

    // Usar el costo del producto o 0 si no tiene
    const costo = product.precio_compra || 0;

    setCart([
      ...cart,
      {
        producto_id: product.id,
        producto_nombre: product.nombre,
        cantidad: 1,
        costo_unitario: costo,
        subtotal: costo
      }
    ]);
    
    toast.success(`${product.nombre} agregado`);
  };

  const updateCartItem = (index, field, value) => {
    const newCart = [...cart];
    newCart[index][field] = parseFloat(value) || 0;
    
    if (field === 'cantidad' || field === 'costo_unitario') {
      newCart[index].subtotal = newCart[index].cantidad * newCart[index].costo_unitario;
    }
    
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handleSaveAdjustment = async () => {
    if (cart.length === 0) {
      toast.error('Agregue al menos un producto');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        tipo: adjustmentData.tipo,
        fecha: new Date(adjustmentData.fecha).toISOString(),
        observaciones: adjustmentData.observaciones,
        items: cart
      };

      await axios.post(`${API}/inventory-adjustments`, payload);
      
      toast.success('Ajuste de inventario registrado exitosamente');
      setShowNewAdjustmentDialog(false);
      resetForm();
      fetchAdjustments();
      fetchProducts(); // Actualizar stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el ajuste');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCart([]);
    setAdjustmentData({
      tipo: 'ENTRADA',
      fecha: new Date().toISOString().split('T')[0],
      observaciones: ''
    });
    setSearchTerm('');
  };

  const viewAdjustmentDetail = async (adjustmentId) => {
    try {
      const response = await axios.get(`${API}/inventory-adjustments/${adjustmentId}`);
      setSelectedAdjustment(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar el detalle');
    }
  };

  const total = calculateTotal();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Ajustes de Inventario</h1>
          <p className="text-slate-600">Entradas y salidas manuales de mercancía</p>
        </div>
        <Button onClick={() => setShowNewAdjustmentDialog(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="mr-2" size={20} />
          Nuevo Ajuste
        </Button>
      </div>

      {/* Lista de Ajustes */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de Ajustes</CardTitle>
          <CardDescription>Últimos ajustes registrados</CardDescription>
        </CardHeader>
        <CardContent>
          {adjustments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package size={48} className="mx-auto mb-2 opacity-50" />
              <p>No hay ajustes registrados</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-3">Tipo</th>
                    <th className="text-left p-3">Fecha</th>
                    <th className="text-left p-3">Total Valorizado</th>
                    <th className="text-left p-3">Usuario</th>
                    <th className="text-left p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {adjustments.map((adjustment) => (
                    <tr key={adjustment.id} className="border-t hover:bg-slate-50">
                      <td className="p-3">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                          adjustment.tipo === 'ENTRADA' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {adjustment.tipo === 'ENTRADA' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                          {adjustment.tipo}
                        </span>
                      </td>
                      <td className="p-3">{new Date(adjustment.fecha).toLocaleDateString()}</td>
                      <td className="p-3 font-semibold">{formatCurrency(adjustment.total)}</td>
                      <td className="p-3">{adjustment.usuario_nombre}</td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewAdjustmentDetail(adjustment.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Eye size={16} className="mr-1" />
                          Ver
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuevo Ajuste */}
      <Dialog open={showNewAdjustmentDialog} onOpenChange={setShowNewAdjustmentDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Nuevo Ajuste de Inventario</DialogTitle>
            <DialogDescription>Registre una entrada o salida manual de mercancía</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Datos Generales */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Información General</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Tipo de Ajuste *</Label>
                    <Select value={adjustmentData.tipo} onValueChange={(value) => setAdjustmentData({ ...adjustmentData, tipo: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ENTRADA">
                          <div className="flex items-center gap-2">
                            <ArrowUp size={16} className="text-green-600" />
                            <span>ENTRADA (Agregar Stock)</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="SALIDA">
                          <div className="flex items-center gap-2">
                            <ArrowDown size={16} className="text-red-600" />
                            <span>SALIDA (Quitar Stock)</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha *</Label>
                    <Input
                      type="date"
                      value={adjustmentData.fecha}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, fecha: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Observaciones / Notas</Label>
                    <Input
                      value={adjustmentData.observaciones}
                      onChange={(e) => setAdjustmentData({ ...adjustmentData, observaciones: e.target.value })}
                      placeholder="Ej: Ajuste por inventario físico"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Búsqueda de Productos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Agregar Productos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                  <Input
                    placeholder="Buscar producto por nombre, código o barras..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {searchTerm && (
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    {filteredProducts.map((product) => (
                      <div
                        key={product.id}
                        className="p-3 hover:bg-slate-50 flex items-center justify-between cursor-pointer"
                        onClick={() => addToCart(product)}
                      >
                        <div>
                          <p className="font-semibold">{product.nombre}</p>
                          <p className="text-sm text-slate-600">
                            Stock: {product.existencia} | Costo: {formatCurrency(product.precio_compra || 0)}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost">
                          <Plus size={18} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Productos Agregados */}
            {cart.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Productos en el Ajuste</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {cart.map((item, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-slate-50">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-semibold">{item.producto_nombre}</p>
                          <Button variant="ghost" size="sm" onClick={() => removeFromCart(index)} className="text-red-600">
                            <Trash2 size={18} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Cantidad</Label>
                            <Input
                              type="number"
                              value={item.cantidad}
                              onChange={(e) => updateCartItem(index, 'cantidad', e.target.value)}
                              min="1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Costo Unitario (referencia)</Label>
                            <Input
                              type="number"
                              value={item.costo_unitario}
                              onChange={(e) => updateCartItem(index, 'costo_unitario', e.target.value)}
                              step="0.01"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Subtotal</Label>
                            <Input
                              type="number"
                              value={item.subtotal.toFixed(2)}
                              disabled
                              className="bg-slate-100"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Total Valorizado */}
                  <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                    <div className="flex justify-between items-center text-xl font-bold text-blue-700">
                      <span>TOTAL VALORIZADO:</span>
                      <span>{formatCurrency(total)}</span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1">Este es el valor total basado en los costos de referencia</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Botones */}
          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowNewAdjustmentDialog(false)} className="flex-1">
              Cancelar
            </Button>
            <Button onClick={handleSaveAdjustment} disabled={loading || cart.length === 0} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Save className="mr-2" size={20} />
              {loading ? 'Guardando...' : 'Guardar Ajuste'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle de Ajuste */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedAdjustment && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-2">
                  {selectedAdjustment.tipo === 'ENTRADA' ? <ArrowUp className="text-green-600" /> : <ArrowDown className="text-red-600" />}
                  Detalle de Ajuste - {selectedAdjustment.tipo}
                </DialogTitle>
                <DialogDescription>
                  Fecha: {new Date(selectedAdjustment.fecha).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4">
                {/* Información General */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Información General</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm text-slate-600">Tipo</Label>
                        <p className="font-semibold">{selectedAdjustment.tipo}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Fecha</Label>
                        <p className="font-semibold">{new Date(selectedAdjustment.fecha).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Usuario</Label>
                        <p className="font-semibold">{selectedAdjustment.usuario_nombre}</p>
                      </div>
                      {selectedAdjustment.observaciones && (
                        <div className="md:col-span-3">
                          <Label className="text-sm text-slate-600">Observaciones</Label>
                          <p className="font-semibold">{selectedAdjustment.observaciones}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Productos */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Productos Ajustados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3">Producto</th>
                            <th className="text-right p-3">Cantidad</th>
                            <th className="text-right p-3">Costo Unit.</th>
                            <th className="text-right p-3">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedAdjustment.items.map((item, index) => (
                            <tr key={index} className="border-t">
                              <td className="p-3 font-medium">{item.producto_nombre}</td>
                              <td className="p-3 text-right">{item.cantidad}</td>
                              <td className="p-3 text-right">{formatCurrency(item.costo_unitario)}</td>
                              <td className="p-3 text-right font-semibold">{formatCurrency(item.subtotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                      <div className="flex justify-between items-center text-xl font-bold text-blue-700">
                        <span>TOTAL VALORIZADO:</span>
                        <span>{formatCurrency(selectedAdjustment.total)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={() => setShowDetailDialog(false)} className="bg-slate-600 hover:bg-slate-700">
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryAdjustments;
